import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';
import type { ConditionalSection } from './intake-form-model';

/**
 * The three modal prompts the intake form raises (R5.10, R5.11, R5.12). Each is a
 * true dialog (focus trap, focus restore, `Esc`) via {@link AriaModalDialog}.
 */

/** The logical field path slugged to a dot-free i18n leaf key (see en.ts note). */
export function discardFieldLabelKey(path: string): string {
  return `intake.discard.field.${path.replace(/\./g, '_')}`;
}

/**
 * The yes→no involvement discard confirmation (R5.10). Names the affected fields;
 * **cancel is a no-op** (values stay in form state) and **confirm** records the
 * intent to prune at submit — the values are not deleted now.
 */
export function DiscardConfirmation({
  section,
  affectedFieldPaths,
  onConfirm,
  onCancel,
}: {
  readonly section: ConditionalSection;
  readonly affectedFieldPaths: readonly string[];
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const sectionLabel = t(`intake.discard.section.${section}`);
  return (
    <AriaModalDialog
      variant="centre"
      isOpen
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      aria-label={t('intake.discard.title', { section: sectionLabel })}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('intake.discard.title', { section: sectionLabel })}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">{t('intake.discard.body')}</p>
      <ul className="mt-2 list-disc pl-5 text-[12.3px] text-txt">
        {affectedFieldPaths.map((path) => (
          <li key={path}>{t(discardFieldLabelKey(path))}</li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('intake.discard.cancel')}
        </Button>
        <Button variant="destructive" size="sm" onClick={onConfirm}>
          {t('intake.discard.confirm')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}

/**
 * The navigate-away-with-unsaved-changes prompt (R5.11). Offers save, discard or
 * continue editing; entered values are retained until save or discard is chosen.
 */
export function UnsavedNavigationPrompt({
  onSave,
  onDiscard,
  onContinue,
}: {
  readonly onSave: () => void;
  readonly onDiscard: () => void;
  readonly onContinue: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <AriaModalDialog
      variant="centre"
      isOpen
      onOpenChange={(open) => {
        if (!open) onContinue();
      }}
      aria-label={t('intake.unsaved.title')}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('intake.unsaved.title')}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">{t('intake.unsaved.body')}</p>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onContinue}>
          {t('intake.unsaved.continue')}
        </Button>
        <Button variant="outline" size="sm" onClick={onDiscard}>
          {t('intake.unsaved.discard')}
        </Button>
        <Button size="sm" onClick={onSave}>
          {t('intake.unsaved.save')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}

/**
 * The post-re-authentication notice (R5.12): after a session ended while the form
 * held unsaved changes, the user is told those changes were not persisted. A
 * dismissible inline banner rather than a modal — the record is unchanged, so
 * this is information, not a decision.
 */
export function SessionEndedNotice({ onDismiss }: { readonly onDismiss: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="mb-3 flex items-start justify-between gap-3 rounded-[var(--radius-control)] border border-[var(--warn)] bg-[#FDF0DC] px-3 py-2 text-[12px] text-[#9A5B00]"
    >
      <span>{t('intake.sessionEnded.message')}</span>
      <button type="button" onClick={onDismiss} className="font-semibold underline">
        {t('intake.sessionEnded.dismiss')}
      </button>
    </div>
  );
}
